import { apiClient } from './api/client';

export type AutomationLogStatus = 'PENDING' | 'RUNNING' | 'FINISHED' | 'FAILED';

export interface AutomationLog {
  id: number;
  user_id: number;
  automation_id: number;
  company_id?: number;
  data?: string;
  success: boolean;
  message?: string;
  metadata?: any;
  status: AutomationLogStatus;
  traceback?: string;
  started_at?: string;
  finished_at?: string;
  screenshot_path?: string;
  created_at: string;
  updated_at: string;
  duration_seconds?: number;
  automation?: {
    id: number;
    name: string;
    endpoint: string;
  };
  user?: {
    id: number;
    name?: string;
    first_name?: string;
    last_name?: string;
    email: string;
  };
  company?: {
    id: number;
    name: string;
  };
}

interface ExecutionHistoryResponse {
  logs: AutomationLog[];
  total: number;
  statusCounts?: {
    PENDING: number;
    RUNNING: number;
    FINISHED: number;
    FAILED: number;
  };
}

export interface ExecutionHistoryFilters {
  userId?: number;
  automationId?: number;
  companyId?: number;
  status?: AutomationLogStatus;
  limit?: number;
  offset?: number;
}

const API_BASE = '/automations/logs';

export const automationLogService = {
  // Buscar histórico de execuções
  async getExecutionHistory(filters?: ExecutionHistoryFilters): Promise<ExecutionHistoryResponse> {
    const params = new URLSearchParams();
    
    if (filters?.userId) params.append('user_id', filters.userId.toString());
    if (filters?.automationId) params.append('automation_id', filters.automationId.toString());
    if (filters?.companyId) params.append('company_id', filters.companyId.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await apiClient.get<ExecutionHistoryResponse>(
      `${API_BASE}/history?${params.toString()}`
    );
    return response.data;
  },

  // Buscar detalhes de uma execução
  async getExecutionDetails(id: number): Promise<AutomationLog> {
    const response = await apiClient.get<AutomationLog>(`${API_BASE}/${id}`);
    return response.data;
  },

  // Atualizar status de uma execução (usado pelo Worker Python)
  async updateStatus(
    id: number,
    update: {
      status?: AutomationLogStatus;
      message?: string;
      traceback?: string;
      screenshot_path?: string;
      started_at?: string;
      finished_at?: string;
    }
  ): Promise<AutomationLog> {
    const response = await apiClient.patch<AutomationLog>(`${API_BASE}/${id}/status`, update);
    return response.data;
  },

  // Upload de screenshot
  async uploadScreenshot(id: number, file: File): Promise<{ success: boolean; screenshot_path: string }> {
    const formData = new FormData();
    formData.append('screenshot', file);

    const response = await apiClient.post<{ success: boolean; screenshot_path: string }>(
      `${API_BASE}/${id}/screenshot`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  // Verificar status da VM
  async getVmStatus(): Promise<{ online: boolean; url?: string; message?: string; status?: number }> {
    const response = await apiClient.get<{ online: boolean; url?: string; message?: string; status?: number }>(
      `${API_BASE}/vm/status`
    );
    return response.data;
  },

  // Buscar automações com status calculado
  async getAutomationsStatus(): Promise<Array<{
    id: number;
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    active: boolean;
    status: 'ESTAVEL' | 'FALHANDO' | 'QUEBRADA' | 'PARADA';
    lastExecutionDate: string | null;
    failedCount: number;
    totalCount: number;
    correctedBy?: number;
    correctedAt?: string;
    correctedByUser?: {
      id: number;
      first_name?: string;
      last_name?: string;
      name?: string;
      email: string;
    };
  }>> {
    const response = await apiClient.get<Array<{
      id: number;
      name: string;
      description?: string;
      icon?: string;
      color?: string;
      active: boolean;
      status: 'ESTAVEL' | 'FALHANDO' | 'QUEBRADA' | 'PARADA';
      lastExecutionDate: string | null;
      failedCount: number;
      totalCount: number;
      correctedBy?: number;
      correctedAt?: string;
      correctedByUser?: {
        id: number;
        first_name?: string;
        last_name?: string;
        name?: string;
        email: string;
      };
    }>>(
      `${API_BASE}/automations/status`
    );
    return response.data;
  },

  // Marcar automação como corrigida
  async markAsCorrected(automationId: number): Promise<{ success: boolean; message: string; updatedCount: number }> {
    const response = await apiClient.post<{ success: boolean; message: string; updatedCount: number }>(
      `${API_BASE}/automations/${automationId}/correct`
    );
    return response.data;
  },

  async getStatistics(filters?: {
    userId?: number;
    automationId?: number;
    companyId?: number;
  }): Promise<{ total: number; successful: number; failed: number; successRate: number }> {
    const params = new URLSearchParams();
    if (filters?.userId) params.append('user_id', filters.userId.toString());
    if (filters?.automationId) params.append('automation_id', filters.automationId.toString());
    if (filters?.companyId) params.append('company_id', filters.companyId.toString());

    const qs = params.toString();
    const response = await apiClient.get<{
      total: number;
      successful: number;
      failed: number;
      successRate: number;
    }>(`${API_BASE}/statistics${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  async getTopAutomations(
    companyId: number,
    limit = 3,
  ): Promise<{ automation_id: number; name: string; count: number }[]> {
    const params = new URLSearchParams();
    params.append('company_id', companyId.toString());
    params.append('limit', limit.toString());

    const response = await apiClient.get<
      { automation_id: number; name: string; count: number }[]
    >(`${API_BASE}/top-automations?${params.toString()}`);
    return response.data ?? [];
  },
};

